// src/Pages/PartnerAccount/Ticket.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ipAddress } from '../../../config';
import './Ticket.css';

const Ticket = () => {
  const partnerId = localStorage.getItem('partnerId');
  const partnerToken = localStorage.getItem('partnerToken');
  const [unredeemedTickets, setUnredeemedTickets] = useState([]);
  const [redeemedTickets, setRedeemedTickets] = useState([]);
  const [unredeemedPagination, setUnredeemedPagination] = useState({});
  const [redeemedPagination, setRedeemedPagination] = useState({});
  // Two distinct states. `loading` is the very first load only — it is the one
  // that swaps the whole page for a spinner. Every later fetch sets `refreshing`
  // instead, which dims the tables in place: unmounting the page mid-search
  // destroys the search input and steals focus from whoever is typing in it.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [redeeming, setRedeeming] = useState({});
  const [error, setError] = useState('');
  const [pageSize, setPageSize] = useState(5);
  const [unredeemedPage, setUnredeemedPage] = useState(1);
  const [redeemedPage, setRedeemedPage] = useState(1);
  // `search` tracks the input; `activeSearch` is the debounced value actually queried.
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  // The ticket awaiting confirmation, or null when the modal is closed.
  const [pendingTicket, setPendingTicket] = useState(null);
  // Transient inline notice: { type: 'success' | 'error', text }.
  const [toast, setToast] = useState(null);

  // Monotonic request id. Debounced typing can leave several fetches in flight at
  // once, and they are not guaranteed to resolve in order — without this, a slow
  // early response can land after a fast later one and overwrite fresh results
  // with stale ones.
  const requestIdRef = useRef(0);
  const didInitialLoadRef = useRef(false);

  // Fetch tickets function
  const fetchTickets = async (redeemed, page = 1, term = '') => {
    try {
      const response = await axios.post(
        `${ipAddress}/api/ContactUsRoutes/GetTicketsByPartner`,
        {
          page,
          limit: pageSize,
          redeemed,
          search: term
        },
        // partnerId is derived from this token server-side; it is no longer sent
        // in the body.
        { headers: { authorization: partnerToken } }
      );

      return {
        tickets: response.data.tickets || [],
        pagination: response.data.pagination || {}
      };
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      throw err;
    }
  };

  // Load tickets.
  // Query inputs are passed in rather than read from state so a call always uses
  // the values it was scheduled with, even if state has moved on since.
  const loadTickets = async (opts = {}) => {
    const {
      term = activeSearch,
      unredeemedTarget = unredeemedPage,
      redeemedTarget = redeemedPage
    } = opts;

    const reqId = requestIdRef.current + 1;
    requestIdRef.current = reqId;

    // Only the first load blanks the page; everything after dims in place.
    if (!didInitialLoadRef.current) setLoading(true);
    else setRefreshing(true);
    setError('');

    try {
      const [unredeemedData, redeemedData] = await Promise.all([
        fetchTickets(false, unredeemedTarget, term),
        fetchTickets(true, redeemedTarget, term)
      ]);

      // A newer request started while this one was in flight — discard this
      // result rather than clobbering the fresher one.
      if (reqId !== requestIdRef.current) return;

      setUnredeemedTickets(unredeemedData.tickets);
      setUnredeemedPagination(unredeemedData.pagination);
      setRedeemedTickets(redeemedData.tickets);
      setRedeemedPagination(redeemedData.pagination);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setError('Failed to load tickets');
    } finally {
      if (reqId === requestIdRef.current) {
        didInitialLoadRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  // Initial load and when dependencies change
  useEffect(() => {
    if (partnerId) {
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, pageSize, unredeemedPage, redeemedPage, activeSearch]);

  // Debounce the search box so typing doesn't fire a query per keystroke.
  // 350ms was too eager at typing speed; 700ms lets a partner finish a code.
  useEffect(() => {
    const term = search.trim();
    if (term === activeSearch) return;

    const timer = setTimeout(() => {
      setActiveSearch(term);
      // Reset to page 1 for the new term, but only when not already there:
      // setting them unconditionally still counts as a dep change and would
      // queue a second fetch for the same search.
      setUnredeemedPage((p) => (p === 1 ? p : 1));
      setRedeemedPage((p) => (p === 1 ? p : 1));
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeSearch]);

  // Auto-dismiss the toast.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Handle redeem ticket — only reached after the partner confirms.
  const handleRedeem = async (ticketId) => {
    setPendingTicket(null);
    setRedeeming(prev => ({ ...prev, [ticketId]: true }));
    try {
      await axios.post(
        `${ipAddress}/api/ContactUsRoutes/RedeemTicket`,
        { ticketId },
        { headers: { authorization: partnerToken } }
      );

      // Refresh tickets after redemption
      await loadTickets();

      // Inline toast rather than alert(): a modal dialog blocks the page and
      // pulls focus out of the search box.
      setToast({ type: 'success', text: 'Ticket redeemed successfully' });
    } catch (err) {
      console.error('Failed to redeem ticket:', err);
      const errorMsg = err.response?.data?.message || 'Failed to redeem ticket';
      setToast({ type: 'error', text: errorMsg });
    } finally {
      setRedeeming(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    // Functional updates so an already-1 page doesn't count as a change and
    // trigger a redundant second fetch alongside the pageSize change.
    setUnredeemedPage((p) => (p === 1 ? p : 1));
    setRedeemedPage((p) => (p === 1 ? p : 1));
  };

  // Voucher code, falling back to a short form of the id for tickets created
  // before codes existed.
  const displayCode = (ticket) =>
    ticket.code || (ticket._id ? `#${ticket._id.slice(-6).toUpperCase()}` : '—');

  const formatDate = (value) =>
    value ? new Date(value).toLocaleString() : '—';

  // Render pagination controls
  const renderPagination = (pagination, currentPage, setPage, label) => {
    if (!pagination || pagination.totalPages <= 1) return null;

    return (
      <div className="pagination-controls">
        <span className="pagination-info">
          {label}: Page {currentPage} of {pagination.totalPages}
          ({pagination.totalTickets} total tickets)
        </span>
        <div className="pagination-buttons">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={!pagination.hasPrevPage}
            className="pagination-btn"
          >
            Previous
          </button>

          {/* Page numbers */}
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
            .filter(page =>
              page === 1 ||
              page === pagination.totalPages ||
              Math.abs(page - currentPage) <= 1
            )
            .map(page => (
              <button
                key={page}
                onClick={() => setPage(page)}
                className={`pagination-btn ${page === currentPage ? 'active' : ''}`}
              >
                {page}
              </button>
            ))
          }

          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={!pagination.hasNextPage}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render ticket table
  const renderTicketTable = (tickets, title, showRedeemButton = false, showConsumedAt = false, pagination = {}) => (
    <div className="ticket-table-section">
      {/* Total across all pages, not just the rows currently rendered. */}
      <h3>{title} ({pagination.totalTickets ?? tickets.length})</h3>
      {tickets.length === 0 ? (
        <p className="no-tickets">
          {activeSearch ? `No tickets match "${activeSearch}"` : 'No tickets found'}
        </p>
      ) : (
        <div className="table-container">
          <table className="tickets-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Voucher</th>
                <th>User</th>
                <th>Restaurant</th>
                <th>Points</th>
                <th>Redeemed Date</th>
                {showConsumedAt && <th>Consumed On</th>}
                {showRedeemButton && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {tickets.map(ticket => (
                <tr key={ticket._id}>
                  <td className="ticket-code">{displayCode(ticket)}</td>
                  <td>{ticket.name || '—'}</td>
                  <td>{ticket.userID}</td>
                  <td>{ticket.restoId}</td>
                  <td>{ticket.points || 0}</td>
                  <td>{ticket.date ? new Date(ticket.date).toLocaleDateString() : 'N/A'}</td>
                  {showConsumedAt && <td>{formatDate(ticket.consumedAt)}</td>}
                  {showRedeemButton && (
                    <td>
                      <button
                        onClick={() => setPendingTicket(ticket)}
                        disabled={redeeming[ticket._id]}
                        className="redeem-btn"
                      >
                        {redeeming[ticket._id] ? 'Redeeming...' : 'Redeem'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Only the very first load replaces the page. Later fetches keep everything
  // mounted so the search input never loses focus mid-type.
  if (loading) return <div className="loading">Loading tickets...</div>;

  return (
    <div className="tickets-container">
      <h2>Ticket Management</h2>

      {error && <div className="error" style={{ color: 'red' }}>{error}</div>}

      {toast && (
        <div className={`ticket-toast ${toast.type}`} role="status">
          {toast.text}
        </div>
      )}

      {/* Search */}
      <div className="search-row">
        <input
          type="text"
          className="ticket-search"
          placeholder="Search by code (WTX-8F3K2Q), voucher, customer or restaurant…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="clear-search-btn" onClick={() => setSearch('')}>
            Clear
          </button>
        )}
        {/* Inline spinner: shows a search is pending without moving anything. */}
        <span className={`search-status ${refreshing ? 'is-busy' : ''}`} aria-hidden="true" />
      </div>

      {/* Page size selector */}
      <div className="controls">
        <label htmlFor="pageSize">Tickets per page:</label>
        <select
          id="pageSize"
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          className="page-size-select"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>

        {/* Arrow function, not a bare reference: onClick would otherwise pass the
            click event in as the options object. */}
        <button onClick={() => loadTickets()} className="refresh-btn" disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Results dim while a fetch is in flight but stay mounted and readable. */}
      <div className={`results-area ${refreshing ? 'is-refreshing' : ''}`}>
        {/* Unredeemed Tickets */}
        {renderTicketTable(unredeemedTickets, 'Unredeemed Vouchers', true, false, unredeemedPagination)}
        {renderPagination(unredeemedPagination, unredeemedPage, setUnredeemedPage, 'Unredeemed')}

        <div className="section-divider"></div>

        {/* Redeemed Tickets */}
        {renderTicketTable(redeemedTickets, 'Redeemed Vouchers', false, true, redeemedPagination)}
        {renderPagination(redeemedPagination, redeemedPage, setRedeemedPage, 'Redeemed')}
      </div>

      {/* Confirmation modal — marking a voucher redeemed cannot be undone. */}
      {pendingTicket && (
        <div className="confirm-overlay" onClick={() => setPendingTicket(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mark voucher as redeemed?</h3>
            <div className="confirm-code">{displayCode(pendingTicket)}</div>
            <ul className="confirm-details">
              <li><span>Voucher</span><b>{pendingTicket.name || '—'}</b></li>
              <li><span>Customer</span><b>{pendingTicket.userID}</b></li>
              <li><span>Restaurant</span><b>{pendingTicket.restoId}</b></li>
            </ul>
            <p className="confirm-warning">
              This confirms the customer has received their voucher. It cannot be undone.
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setPendingTicket(null)}>
                Cancel
              </button>
              <button className="confirm-accept" onClick={() => handleRedeem(pendingTicket._id)}>
                Yes, mark as redeemed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ticket;
